import styled, { keyframes } from 'styled-components'

export const StyledTerminalWrapper = styled.div`
  --terminal-header-height: 30px;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  box-sizing: border-box;
  font-size: 14px;
  width: 100%;
  height: 100vh;
  min-height: 100vh;
  height: 100dvh;
  min-height: 100dvh;
  max-width: 100%;
  margin: 0 !important;
  padding-top: env(safe-area-inset-top);
  padding-right: env(safe-area-inset-right);
  padding-bottom: env(safe-area-inset-bottom);
  padding-left: env(safe-area-inset-left);
  border-radius: 0;
  color: white;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  box-shadow: none;
  overflow: hidden;
  z-index: 9999;

  @media screen and (max-width: 760px) {
    width: 100%;
    margin-top: 0 !important;
    font-size: 13px;
  }

  @media screen and (max-width: 420px) {
    --terminal-header-height: 28px;
    font-size: 12px;
  }
`

export const StyledTerminal = styled.div`
  position: absolute;
  top: var(--terminal-header-height);
  left: 0;
  right: 0;
  bottom: 0;
  overflow: auto;
  overscroll-behavior: contain;
  -webkit-overflow-scrolling: touch;
  z-index: 1;
  margin-top: 0;
  height: auto;
  max-height: none;
  background-color: rgba(0, 0, 0, 0.95);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border-radius: 0;

  @media screen and (max-width: 760px) {
    box-shadow: none;
  }

  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: rgba(0, 0, 0, 0.1);
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.2);
    border-radius: 4px;
  }
`

export const StyledTerminalInner = styled.div`
  box-sizing: border-box;
  min-height: 100%;
  padding: 20px 24px 24px;
  padding-bottom: calc(24px + env(safe-area-inset-bottom));
  font-weight: normal;
  font-family: "SF Mono", "Fira Code", "Source Code Pro", Monaco, Menlo,
    Consolas, monospace;
  color: #fff;
  line-height: 1.5;

  @media screen and (max-width: 760px) {
    padding: 16px 14px 20px;
    padding-bottom: calc(20px + env(safe-area-inset-bottom));
  }

  @media screen and (max-width: 420px) {
    padding: 14px 12px 18px;
    padding-bottom: calc(18px + env(safe-area-inset-bottom));
  }
`

export const StyledInputWrapper = styled.p`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  margin: 0;
  padding: 8px 0;
  width: 100%;
  row-gap: 4px;
  word-spacing: 0;
  letter-spacing: 0;
  overflow-wrap: anywhere;
  word-break: break-word;
  font-weight: 400;
  font-family: "SF Mono", Fira Code, Source Code Pro, Monaco, Menlo, Consolas,
    monospace;
  color: #fff;
`

export const StyledInput = styled.input`
  position: absolute;
  background: transparent;
  border: none;
  width: 1px;
  height: 1px;
  opacity: 0;
  padding: 0;
  margin: 0;
  cursor: default;
  pointer-events: none;
  z-index: -1;

  &:focus {
    outline: none;
    border: none;
  }
`

export const StyledPrompt = styled.span`
  flex: 0 1 auto;
  word-break: break-all;
  overflow-wrap: anywhere;
  color: #56b6c2;
  margin-right: 8px;
`

const loadingDots = keyframes`
  0%, 20% {
    color: rgba(255, 255, 255, 0);
    text-shadow: 0.25em 0 0 rgba(255, 255, 255, 0), 0.5em 0 0 rgba(255, 255, 255, 0);
  }
  40% {
    color: white;
    text-shadow: 0.25em 0 0 rgba(255, 255, 255, 0), 0.5em 0 0 rgba(255, 255, 255, 0);
  }
  60% {
    text-shadow: 0.25em 0 0 white, 0.5em 0 0 rgba(255, 255, 255, 0);
  }
  80%, 100% {
    text-shadow: 0.25em 0 0 white, 0.5em 0 0 white;
  }
`

export const StyledLoadingCursor = styled.span`
  font: 300 1.5em "SF Mono";
  animation: ${loadingDots} 1s steps(5, end) infinite;
`

const blinkDot = keyframes`
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0;
  }
`

export const StyledBlinkCursor = styled.span`
  position: relative;
  display: inline-flex;
  align-items: center;
  width: 8px;
  height: 17px;
  line-height: 17px;
  vertical-align: middle;
  color: inherit;
  margin-left: 0;
  z-index: 1;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: ${props => props.children === '\u00A0' ? '#56b6c2' : 'rgba(86, 182, 194, 0.7)'};
    animation: ${blinkDot} 1s step-end infinite;
  }

  ${props => props.children !== '\u00A0' && `
    &::after {
      content: '';
      position: absolute;
      left: 0;
      z-index: 2;
      color: #abb2bf;
    }
  `}
`

export const StyledLine = styled.div`
  overflow-wrap: anywhere;
  word-break: break-word;
  margin: 8px 0;
  line-height: 1.5;

  &.hero {
    margin: 0 0 18px;
    padding: 14px 16px;
    font-size: 20px;
    font-weight: 700;
    line-height: 1.7;
    color: #f8fafc;
    background: linear-gradient(135deg, rgba(37, 99, 235, 0.35), rgba(14, 165, 233, 0.2));
    border: 1px solid rgba(125, 211, 252, 0.45);
    border-radius: 12px;
    box-shadow: 0 10px 30px rgba(14, 165, 233, 0.12);
    word-break: normal;
  }

  &.hero .cmd {
    color: #f8fafc;
  }

  .cmd {
    color: #abb2bf;
    overflow-wrap: anywhere;
    word-break: break-word;
  }

  .info {
    color: #61afef;
  }

  .warning {
    color: #e5c07b;
  }

  .success {
    color: #98c379;
  }

  .error {
    color: #e06c75;
  }

  .system {
    color: #c678dd;
  }

  .time {
    color: #56b6c2;
  }

  .black {
    color: #4b5263;
  }

  .time,
  .system,
  .error,
  .success,
  .warning,
  .info,
  .black {
    display: inline-block;
    margin-right: 8px;
    margin-bottom: 6px;
    padding: 2px 4px;
    border-radius: 3px;
    background: rgba(255, 255, 255, 0.1);
  }

  @media screen and (max-width: 760px) {
    margin: 6px 0;
    line-height: 1.6;

    &.hero {
      margin-bottom: 14px;
      padding: 12px 14px;
      font-size: 17px;
      line-height: 1.6;
      border-radius: 10px;
    }
  }

  @media screen and (max-width: 420px) {
    &.hero {
      padding: 10px 12px;
      font-size: 15px;
    }
  }
`

export const StyledHeader = styled.div`
  position: absolute;
  z-index: 2;
  top: 0;
  right: 0;
  left: 0;
  box-sizing: border-box;
  height: var(--terminal-header-height);
  padding: 0 16px;
  background: rgba(30, 30, 30, 0.95);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border-radius: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  -webkit-app-region: drag;

  @media screen and (max-width: 420px) {
    padding: 0 12px;
  }
`

export const StyledHeaderTitle = styled.h4`
  box-sizing: border-box;
  margin: 0;
  width: 100%;
  max-width: 100%;
  padding: 0 72px;
  overflow: hidden;
  font-size: 13px;
  letter-spacing: 0.5px;
  font-weight: 400;
  color: rgba(255, 255, 255, 0.8);
  text-align: center;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica,
    Arial, sans-serif;

  @media screen and (max-width: 420px) {
    padding: 0 56px;
    font-size: 12px;
  }
`

export const StyledHeaderDotList = styled.ul`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  left: 16px;
  padding: 0;
  margin: 0;
  display: flex;
  gap: 6px;
  list-style: none;

  @media screen and (max-width: 420px) {
    left: 12px;
    gap: 5px;
  }
`

export const StyledHeaderDotItem = styled.li`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background-color: ${(props) => {
    switch (props.color) {
      case 'red':
        return '#ff5f56'
      case 'yellow':
        return '#ffbd2e'
      case 'green':
        return '#27c93f'
      default:
        return '#ddd'
    }
  }};
  transition: all 0.2s ease;
  cursor: pointer;

  &:hover {
    filter: brightness(0.8);
  }

  @media screen and (max-width: 420px) {
    width: 10px;
    height: 10px;
  }
`

export const StyledCommand = styled.span`
  font-size: inherit;
  color: inherit;
  font-family: inherit;
  line-height: inherit;
  vertical-align: middle;
  overflow-wrap: anywhere;
  word-break: break-word;
`
